import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, of } from 'rxjs';
import {
  AngularMaterialService,
  ErrorParseService,
  LGEDataService,
  ISetPassword,
  AuthenticationService,
} from 'src/commons';

@Component({
  selector: 'set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetPasswordComponent implements OnInit {
  setPasswordFormGroup: FormGroup;

  sid: string = '';
  guid: string = '';
  email: string = '';
  isVerifiedUser: boolean = false;

  constructor(
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private errorParse: ErrorParseService,
    private lgeDataService: LGEDataService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private toastrService: ToastrService
  ) {
    this.createForms();
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe((res: any) => {
      if (res && res.keys.length > 0) {
        this.sid = res.params['sid'];
        this.guid = res.params['guid'];
        this.email = res.params['email'];
        (<FormControl>this.setPasswordFormGroup.controls['email']).setValue(
          this.email
        );
        (<FormControl>this.setPasswordFormGroup.controls['sid']).setValue(
          this.sid
        );
      }
    });

    if (this.guid) {
      this.verifyUserByGUID();
    } else if (this.sid) {
      this.isVerifiedUser = true;
      const session = {
        guid: this.sid,
        userId: null,
        applicationID: 'CCMSEXT',
      };
      // this.authService.validateSession(session).subscribe((res) => {
      //   if (!res) {
      //     console.error('EXPIRED');
      //     this.ams.showAlert(
      //       'EXPIRED',
      //       'Your password reset link has been expired. Please enter your email address next to receive a new password reset link'
      //     );
      //     this.ams.alert
      //       .afterClosed()
      //       .subscribe(() => this.router.navigate([`forgot-password`]));
      //   } else {
      //     console.log('VALID');
      //   }
      // });
    } else {
      this.isVerifiedUser = false;
    }
  }

  //go back to forgot-password page
  forgotPassword() {
    this.router.navigate([`forgot-password`]);
  }

  updateStatus() {
    this.lgeDataService.UpdateExtProfileAccessStatusMVC(this.email).subscribe({
      next: (result) => {
        if (result) {
          console.log(result);
        }
      },
      error: (error) => {
        this.ams.closeDialog();
        this.ams.showAlert(
          'Error!',
          `Please try again later and/or contact contributions@lge-ku.com for further support. \n Details:${error.message}`
        );
        this.ref.markForCheck();
      },
    });
  }

  setPassword() {
    if (this.isVerifiedUser) {
      let loginInfo: ISetPassword = this.setPasswordFormGroup.value;
      this.ams.showLoader('Saving...');
      this.lgeDataService.setPassword(loginInfo).subscribe({
        next: (response) => {
          if (response && !response.errorExists) {
            if (this.email && !this.sid) {
              this.lgeDataService
                .UpdateExtProfileAccessStatusMVC(this.email)
                .subscribe({
                  next: (result) => {
                    this.ams.closeDialog();
                    if (result && result.data === true) {
                      this.ams.showAlert(
                        'Success!',
                        'Password has been set up successfully!'
                      );
                      this.postRequestCompleted();
                    }
                  },
                  error: (error) => {
                    this.ams.closeDialog();
                    this.ams.showAlert(
                      'Error!',
                      `Please try again later and/or contact contributions@lge-ku.com for further support. \n Details:${error.message}`
                    );
                  },
                });
            } else {
              this.ams.closeDialog();
              this.ams.showAlert(
                'Success!',
                'Password has been updated successfully!'
              );
              this.postRequestCompleted();
            }
          } else {
            this.ams.closeDialog();
            if (response?.message.includes('was not active')) {
              this.ams.showAlert(
                'EXPIRED',
                'Your password reset link has been expired. Please enter your email address next to receive a new password reset link'
              );
              this.ams.alert
                .afterClosed()
                .subscribe(() =>
                  this.router.navigate([`forgot-password`])
                );
            } else
            this.ams.showAlert(
              'Error!',
              `Please try again later and/or contact contributions@lge-ku.com for further support. \n Details:${response}`
            );
          }
          this.ref.markForCheck();
        },
        error: (error) => {
          this.ams.closeDialog();
          this.ams.showAlert(
            'Error!',
            `Please try again later and/or contact contributions@lge-ku.com for further support. \n Details:${error.message}`
          );
          this.ref.markForCheck();
        },
      });
    } else {
      this.ams.showAlert(
        'Unauthorized!',
        `You are not authorized to perform any action on this page.`
      );
    }
  }

  postRequestCompleted() {
    let leaving = setTimeout(() => {
      this.createForms();
      this.router.navigate([`login`]);
    }, 5000);
    this.ams.alert.afterClosed().subscribe(() => {
      this.createForms();
      this.router.navigate([`login`]);
      clearTimeout(leaving);
    });
  }

  verifyUserByGUID() {
    this.lgeDataService.VerifyUserByGUIDMVC(this.email, this.guid).subscribe({
      next: (result) => {
        if (result && !result.errorExists) {
          this.isVerifiedUser = result.data;
        } else {
          this.isVerifiedUser = false;
        }
        this.ref.markForCheck();
      },
      error: (error) => {
        this.ams.closeDialog();
        this.ams.showAlert(
          'Error!',
          `Could not verify the user! \n Please try again later and/or contact contributions@lge-ku.com for further support. \n Details:${error.message}`
        );
        this.ref.markForCheck();
      },
    });
  }

  createForms(): void {
    this.setPasswordFormGroup = this.fb.group(
      {
        email: [''],
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
        sid: [''],
      },
      {
        validator: ConfirmedValidator('password', 'confirmPassword'),
      }
    );

    function ConfirmedValidator(
      controlName: string,
      matchingControlName: string
    ) {
      return (formGroup: FormGroup) => {
        const control = formGroup.controls[controlName];
        const matchingControl = formGroup.controls[matchingControlName];
        if (
          matchingControl.errors &&
          !matchingControl.errors['confirmedValidator']
        ) {
          return;
        }
        if (control.value !== matchingControl.value) {
          matchingControl.setErrors({ confirmedValidator: true });
        } else {
          matchingControl.setErrors(null);
        }
      };
    }
  }

  displayLoader(pending: boolean): void {
    if (pending) {
      this.setPasswordFormGroup.disable();
      this.ams.showLoader('Saving');
    } else {
      this.setPasswordFormGroup.enable();
      this.ams.closeDialog().then(() => {
        // if (this.error !== null && this.error !== undefined && this.error !== '') {
        //   this.displayError(this.error);
        // }
      });
    }
  }

  displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, 'CORPCOMMONWS')) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    this.ams.showAlert('Error', errorMessage);
    this.setPasswordFormGroup.reset();
  }
}
